import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchCompanySettings, updateCompanySettings, fetchFinancialYears, createFinancialYear, selectFinancialYear,
} from '../api/resources';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fmtDate } from '../utils/format';
import Spinner from '../components/ui/Spinner';
import Modal from '../components/ui/Modal';
import Field from '../components/ui/Field';
import PageHeader from '../components/ui/PageHeader';
import Toggle from '../components/ui/Toggle';
import { Settings as SettingsIcon } from 'lucide-react';
import { STATES } from '../utils/india';

export default function Settings() {
  const { companyId, role, currentCompany, selectFY, fyId } = useAuth();
  const { success, error } = useToast();
  const qc = useQueryClient();
  const isAdmin = role === 'ADMIN';

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['company-settings', companyId],
    queryFn: () => fetchCompanySettings(companyId),
    enabled: !!companyId,
  });

  const { data: fyData } = useQuery({
    queryKey: ['financial-years', companyId],
    queryFn: () => fetchFinancialYears(companyId),
    enabled: !!companyId,
  });
  const years = fyData?.financialYears || [];

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    if (settingsData?.company && !form) {
      const c = settingsData.company;
      setForm({
        name: c.name || '', address: c.address || '', country: c.country || 'India',
        state: c.state || '', city: c.city || '', phone: c.phone || '',
        email: c.email || '', gstin: c.gstin || '',
        enableWorkerWiseRates: !!c.enableWorkerWiseRates,
        gstEnabled: !!c.gstEnabled,
        singleCuttingMaster: !!c.singleCuttingMaster,
      });
    }
  }, [settingsData, form]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form?.name) return error('Company name is required.');
    setSaving(true);
    try {
      const res = await updateCompanySettings(companyId, form);
      qc.invalidateQueries({ queryKey: ['auth'] });
      success(res.message);
    } catch (x) {
      error(x.response?.data?.message || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  const activateFY = async (id) => {
    try {
      await selectFinancialYear(companyId, id);
      await selectFY(id);
      success('Financial year switched.');
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    } catch {
      error('Could not switch financial year.');
    }
  };

  if (isLoading || !form) return <Spinner label="Loading settings…" />;

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <PageHeader
        icon={SettingsIcon}
        eyebrow="Admin"
        title="Company Settings"
        subtitle="Company profile, financial years and billing preferences"
      />

      {/* Company profile */}
      <div className="card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-brand-900">Company Profile</h3>
          {!isAdmin && <span className="rounded-full bg-surface-hover px-2 py-0.5 text-xs text-content-muted">Read-only — Admin only</span>}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Company Name" required><input className="input" value={form.name} onChange={set('name')} disabled={!isAdmin} /></Field>
          <Field label="Phone"><input className="input" value={form.phone} onChange={set('phone')} disabled={!isAdmin} /></Field>
          <Field label="Email"><input className="input" type="email" value={form.email} onChange={set('email')} disabled={!isAdmin} /></Field>
          <Field label="GSTIN"><input className="input" value={form.gstin} onChange={set('gstin')} disabled={!isAdmin} /></Field>
          <Field label="Country"><input className="input" value={form.country} onChange={set('country')} disabled={!isAdmin} /></Field>
          <Field label="State">
            <select className="input" value={form.state} onChange={set('state')} disabled={!isAdmin}>
              <option value="">Select state…</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="City"><input className="input" value={form.city} onChange={set('city')} disabled={!isAdmin} /></Field>
          <Field label="Address" className="sm:col-span-2"><textarea className="input min-h-[70px]" value={form.address} onChange={set('address')} disabled={!isAdmin} /></Field>
        </div>

        {/* How worker pay is costed, and whether tax is charged at all. */}
        <div className="mt-4 space-y-3">
          <div className="rounded-lg border border-line bg-surface-sunken p-3">
            <Toggle
              checked={!!form.enableWorkerWiseRates}
              disabled={!isAdmin}
              onChange={(v) => setForm((f) => ({ ...f, enableWorkerWiseRates: v }))}
              label="Worker-wise rates"
              hint="Off: every job is paid at the item's standard cutting / stitching rate. On: a rate set for a worker (Worker & Item Rates) is used instead, where one exists. Work already assigned keeps the rate it was costed at either way."
            />
          </div>
          <div className="rounded-lg border border-line bg-surface-sunken p-3">
            <Toggle
              checked={!!form.singleCuttingMaster}
              disabled={!isAdmin}
              onChange={(v) => setForm((f) => ({ ...f, singleCuttingMaster: v }))}
              label="Single Cutting Master"
              hint="On: the cloth for an order is cut in one go, so one cutting master is chosen once for the whole order. Off: each garment's cutting is handed out on its own row, like its stitching. Orders already assigned keep the cutters they were given either way."
            />
          </div>
          <div className="rounded-lg border border-line bg-surface-sunken p-3">
            <Toggle
              checked={!!form.gstEnabled}
              disabled={!isAdmin}
              onChange={(v) => setForm((f) => ({ ...f, gstEnabled: v }))}
              label="Charge GST"
              hint="Off: every order is billed at zero tax and the GST boxes stay out of the way. On: each garment is taxed at its own rate and CGST / SGST appear on the bill. Orders already saved keep the tax they were billed with either way."
            />
          </div>
        </div>
        {isAdmin && (
          <div className="mt-4 flex justify-end">
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Profile'}</button>
          </div>
        )}
      </div>

      {/* Financial years */}
      <div className="card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-brand-900">Financial Years</h3>
          {isAdmin && <button className="btn-outline !py-1.5 text-xs" onClick={() => setShowAdd(true)}>+ Add Year</button>}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-surface-sunken">
            <tr><th className="th">Year</th><th className="th">Start</th><th className="th">End</th><th className="th text-right">Active</th></tr>
          </thead>
          <tbody>
            {years.map((y) => (
              <tr key={y.id} className="border-t border-line">
                <td className="td font-medium">{y.label}</td>
                <td className="td">{fmtDate(y.startDate)}</td>
                <td className="td">{fmtDate(y.endDate)}</td>
                <td className="td text-right">
                  {String(y.id) === String(fyId) ? (
                    <span className="badge-success">Active</span>
                  ) : (
                    <button className="text-xs font-semibold text-brand-700 hover:underline" onClick={() => activateFY(y.id)}>Set Active</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-content-subtle">Currently viewing: <b>{currentCompany?.name}</b>. All invoices and reports are scoped to the active financial year.</p>
      </div>

      {/* Team */}
      <div className="card p-5">
        <h3 className="mb-3 font-display text-lg font-semibold text-brand-900">Team Members</h3>
        <table className="w-full text-sm">
          <thead className="bg-surface-sunken"><tr><th className="th">Name</th><th className="th">Role</th></tr></thead>
          <tbody>
            {currentCompany ? (
              <tr className="border-t border-line"><td className="td font-medium">You</td><td className="td">{currentCompany.role}</td></tr>
            ) : null}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-content-subtle">Team management is available at the tenant level.</p>
      </div>

      <AddYearModal open={showAdd} onClose={() => setShowAdd(false)} companyId={companyId} onAdded={() => { setShowAdd(false); qc.invalidateQueries({ queryKey: ['financial-years'] }); }} />
    </div>
  );
}

function AddYearModal({ open, onClose, companyId, onAdded }) {
  const { success, error } = useToast();
  const [label, setLabel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!label) return error('Year label is required.');
    setSaving(true);
    try {
      const res = await createFinancialYear(companyId, { label, startDate, endDate });
      success(res.message);
      onAdded();
    } catch (x) {
      error(x.response?.data?.message || 'Could not add year.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Financial Year" size="sm"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Adding…' : 'Add Year'}</button>
      </>}>
      <div className="space-y-3">
        <Field label="Year Label" required hint="e.g. 2027 – 2028"><input className="input" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus /></Field>
        <Field label="Start Date"><DateInputNative value={startDate} onChange={setStartDate} /></Field>
        <Field label="End Date"><DateInputNative value={endDate} onChange={setEndDate} /></Field>
      </div>
    </Modal>
  );
}

function DateInputNative({ value, onChange }) {
  return <input type="date" className="input" value={value} onChange={(e) => onChange(e.target.value)} />;
}
